import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
    NotesWithMentionsDto,
    PolicyNoteRequest,
    TimeOffPolicyNoteType,
    TimeOffResourcePolicyNotesDto,
    UpdateAssignmentNotesRequest,
    UpdateMatterNotesRequest,
    UpdateResourceNotesRequest,
    UpdateTimeOffNotesRequest,
} from '../models/notes.models';

@Injectable({
  providedIn: 'root',
})
export class NotesService {
  private http = inject(HttpClient);

  private readonly resourceApiUrl = `${environment.apiUrl}/api/resources`;
  private readonly mattersApiUrl = `${environment.apiUrl}/api/matters`;
  private readonly assignmentsApiUrl = `${environment.apiUrl}/api/requests/assignments`;
  private readonly timeOffApiUrl = `${environment.apiUrl}/api/timeoff`;

  /**
   * Update notes for a resource
   * @param employeeId The employee ID
   * @param notes The notes content (rich text HTML)
   * @returns Observable of the updated NotesWithMentionsDto
   */
  updateResourceNotes(
    employeeId: string,
    notes: string,
  ): Observable<NotesWithMentionsDto> {
    const request: UpdateResourceNotesRequest = { notes };
    return this.http.put<NotesWithMentionsDto>(
      `${this.resourceApiUrl}/${employeeId}/notes`,
      request,
    );
  }

  /**
   * Update notes for a matter
   * @param clientMatterNumber The client-matter number
   * @param notes The notes content (rich text HTML)
   * @returns Observable of the updated NotesWithMentionsDto
   */
  updateMatterNotes(
    clientMatterNumber: string,
    notes: string,
  ): Observable<NotesWithMentionsDto> {
    const request: UpdateMatterNotesRequest = { notes };
    return this.http.put<NotesWithMentionsDto>(
      `${this.mattersApiUrl}/${clientMatterNumber}/notes`,
      request,
    );
  }

  /**
   * Update the manager-authored note on an assignment.
   * Requires Assignments.ManagerNotes.Write. Empty/whitespace notes are
   * rejected with 400; use deleteAssignmentNotes to clear a note instead.
   * @param assignedResourceId The assigned resource ID
   * @param notes The notes content (rich text HTML)
   * @returns Observable of the updated NotesWithMentionsDto
   */
  updateAssignmentNotes(
    assignedResourceId: number,
    notes: string,
  ): Observable<NotesWithMentionsDto> {
    const request: UpdateAssignmentNotesRequest = { notes };
    return this.http.put<NotesWithMentionsDto>(
      `${this.assignmentsApiUrl}/${assignedResourceId}/manager-notes`,
      request,
    );
  }

  /**
   * Delete the manager-authored note on an assignment.
   * Requires Assignments.ManagerNotes.Write, same as updateAssignmentNotes.
   * Returns 404 when no note exists.
   * @param assignedResourceId The assigned resource ID
   */
  deleteAssignmentNotes(assignedResourceId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.assignmentsApiUrl}/${assignedResourceId}/manager-notes`,
    );
  }

  /**
   * Upsert the current user's own note on an assignment (resource note).
   * Ownership-only: the API rejects callers who are not the assigned
   * resource — no write permission exists for this note type by design.
   * Empty/whitespace notes are rejected with 400; use
   * deleteAssignmentResourceNotes to clear a note instead.
   * @param assignedResourceId The assigned resource ID
   * @param notes The notes content
   * @returns Observable of the updated NotesWithMentionsDto
   */
  updateAssignmentResourceNotes(
    assignedResourceId: number,
    notes: string,
  ): Observable<NotesWithMentionsDto> {
    const request: UpdateAssignmentNotesRequest = { notes };
    return this.http.put<NotesWithMentionsDto>(
      `${this.assignmentsApiUrl}/${assignedResourceId}/resource-notes`,
      request,
    );
  }

  /**
   * Delete the current user's own note on an assignment (resource note).
   * Ownership-only, same as updateAssignmentResourceNotes. Returns 404
   * when no note exists.
   * @param assignedResourceId The assigned resource ID
   */
  deleteAssignmentResourceNotes(assignedResourceId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.assignmentsApiUrl}/${assignedResourceId}/resource-notes`,
    );
  }

  /**
   * Get notes for a time off request
   * @param timeOffRequestId The time off request ID
   * @returns Observable of NotesWithMentionsDto
   */
  getTimeOffNotes(
    timeOffRequestId: number,
  ): Observable<NotesWithMentionsDto> {
    return this.http.get<NotesWithMentionsDto>(
      `${this.timeOffApiUrl}/${timeOffRequestId}/notes`,
    );
  }

  /**
   * Update notes for a time off request
   * @param timeOffRequestId The time off request ID
   * @param notes The notes content (rich text HTML)
   * @returns Observable of the updated NotesWithMentionsDto
   */
  updateTimeOffNotes(
    timeOffRequestId: number,
    notes: string,
  ): Observable<NotesWithMentionsDto> {
    const request: UpdateTimeOffNotesRequest = { notes };
    return this.http.put<NotesWithMentionsDto>(
      `${this.timeOffApiUrl}/${timeOffRequestId}/notes`,
      request,
    );
  }

  /**
   * Get notes for the current user's time off request
   * @param timeOffRequestId The time off request ID
   * @returns Observable of NotesWithMentionsDto
   */
  getMyTimeOffNotes(
    timeOffRequestId: number,
  ): Observable<NotesWithMentionsDto> {
    return this.http.get<NotesWithMentionsDto>(
      `${this.timeOffApiUrl}/my/${timeOffRequestId}/notes`,
    );
  }

  /**
   * Update notes for the current user's time off request
   * @param timeOffRequestId The time off request ID
   * @param notes The notes content (rich text HTML)
   * @returns Observable of the updated NotesWithMentionsDto
   */
  updateMyTimeOffNotes(
    timeOffRequestId: number,
    notes: string,
  ): Observable<NotesWithMentionsDto> {
    const request: UpdateTimeOffNotesRequest = { notes };
    return this.http.put<NotesWithMentionsDto>(
      `${this.timeOffApiUrl}/my/${timeOffRequestId}/notes`,
      request,
    );
  }

  /**
   * Get all (vacation/sick/bereavement) policy notes for an employee.
   * Notes are decoupled from the existence of a TimeOffResourcePolicy row.
   * @param employeeId The employee ID
   */
  getResourcePolicyNotes(
    employeeId: string,
  ): Observable<TimeOffResourcePolicyNotesDto> {
    return this.http.get<TimeOffResourcePolicyNotesDto>(
      `${this.timeOffApiUrl}/resource-policies/employee/${employeeId}/notes`,
    );
  }

  /**
   * Upsert a single policy note (vacation/sick/bereavement) for an employee.
   * @param employeeId The employee ID
   * @param noteType The slot to write to
   * @param notes The note content
   */
  updateResourcePolicyNote(
    employeeId: string,
    noteType: TimeOffPolicyNoteType,
    notes: string,
  ): Observable<NotesWithMentionsDto> {
    const request: PolicyNoteRequest = { noteType, notes };
    return this.http.put<NotesWithMentionsDto>(
      `${this.timeOffApiUrl}/resource-policies/employee/${employeeId}/notes`,
      request,
    );
  }

  /**
   * Delete a single policy note (vacation/sick/bereavement) for an employee.
   * @param employeeId The employee ID
   * @param noteType The slot to delete
   */
  deleteResourcePolicyNote(
    employeeId: string,
    noteType: TimeOffPolicyNoteType,
  ): Observable<void> {
    return this.http.request<void>(
      'delete',
      `${this.timeOffApiUrl}/resource-policies/employee/${employeeId}/notes`,
      { body: { noteType } },
    );
  }
}
